import ApiService from './api'

class SupplierReturnService {
  /**
   * Get all supplier return notes
   * @param {Object} params - Query parameters for filtering and pagination
   * @returns {Promise} API response
   */
  async getAllSupplierReturnNotes(params = {}) {
    try {
      console.log('🔄 SupplierReturnService: Fetching supplier return notes with params:', params)
      const response = await ApiService.get('/supplier-return-notes', params)
      console.log('✅ SupplierReturnService: API response received:', response.data)
      return response.data
    } catch (error) {
      console.error('Error fetching supplier return notes:', error)
      
      // Return mock data if API is not available
      if (error.message === 'Not found' || error.response?.status === 404) {
        console.warn('Supplier return notes API not available, returning mock data')
        return {
          success: true,
          data: {
            supplierReturnNotes: [],
            pagination: {
              total: 0,
              page: 1,
              totalPages: 0
            }
          }
        }
      }
      throw error
    }
  }

  /**
   * Get supplier return note by ID
   * @param {string} id - Supplier return note ID
   * @returns {Promise} API response
   */
  async getSupplierReturnNoteById(id) {
    try {
      const response = await ApiService.get(`/supplier-return-notes/${id}`)
      return response.data
    } catch (error) {
      console.error('Error fetching supplier return note:', error)
      throw error
    }
  }

  /**
   * Create a new supplier return note
   * @param {Object} supplierReturnData - Supplier return note data
   * @param {string} supplierReturnData.purchaseOrder - Purchase order ID
   * @param {string} supplierReturnData.returnReason - Reason for return
   * @param {Array} supplierReturnData.returnedItems - Array of returned items
   * @param {string} supplierReturnData.note - Optional note
   * @returns {Promise} API response
   */
  async createSupplierReturnNote(supplierReturnData) {
    try {
      console.log('🔄 Creating supplier return note:', supplierReturnData)
      
      // Validate required fields
      if (!supplierReturnData.purchaseOrder) {
        throw new Error('Purchase Order is required')
      }
      if (!supplierReturnData.returnedItems || supplierReturnData.returnedItems.length === 0) {
        throw new Error('At least one item must be selected for return')
      }
      
      const response = await ApiService.post('/supplier-return-notes', supplierReturnData)
      console.log('✅ Supplier return note created successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error creating supplier return note:', error)
      
      // Enhanced error handling with specific messages
      if (error.response?.status === 400) {
        const message = error.response?.data?.message || 'Invalid supplier return data provided'
        throw new Error(message)
      } else if (error.response?.status === 404) {
        throw new Error('Purchase order not found')
      } else if (error.response?.status === 409) {
        throw new Error('Supplier return note already exists for this purchase order')
      } else if (error.response?.status === 422) {
        throw new Error('Return quantity exceeds available quantity')
      } else if (error.message?.includes('Network Error')) {
        throw new Error('Network error. Please check your connection and try again')
      }
      
      throw error
    }
  }

  /**
   * Update a supplier return note
   * @param {string} id - Supplier return note ID
   * @param {Object} updateData - Updated supplier return note data
   * @returns {Promise} API response
   */
  async updateSupplierReturnNote(id, updateData) {
    try {
      console.log('🔄 Updating supplier return note:', { id, updateData })
      
      const response = await ApiService.patch(`/supplier-return-notes/${id}`, updateData)
      console.log('✅ Supplier return note updated successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error updating supplier return note:', error)
      
      // Enhanced error handling
      if (error.response?.status === 400) {
        const message = error.response?.data?.message || 'Invalid update data provided'
        throw new Error(message)
      } else if (error.response?.status === 404) {
        throw new Error('Supplier return note not found')
      } else if (error.response?.status === 422) {
        throw new Error('Return quantity exceeds available quantity')
      } else if (error.message?.includes('Network Error')) {
        throw new Error('Network error. Please check your connection and try again')
      }
      
      throw error
    }
  }

  /**
   * Get supplier return statistics
   * @param {Object} params - Query parameters for filtering
   * @returns {Promise} API response with statistics
   */
  async getSupplierReturnStatistics(params = {}) {
    try {
      const response = await ApiService.get('/supplier-return-notes/statistics', params)
      return response.data
    } catch (error) {
      console.error('Error fetching supplier return statistics:', error)
      
      // Return mock statistics if API is not available
      return {
        success: true,
        data: {
          totalReturns: 0,
          thisMonthReturns: 0,
          totalReturnAmount: 0,
          avgReturnAmount: 0
        }
      }
    }
  }
}

export default new SupplierReturnService()